import { Component, Inject, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';

import { Subject, combineLatest, forkJoin, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { UserService } from '../services/user.service';
import { UserTLR } from '../entities/user-tlr.entity';
import { UserPreferenceNotificationBase } from '../entities/user-preference-notification-base';

import { ParcService } from '../../../structure/parc/services/parc.service';

class Header extends UserPreferenceNotificationBase {
	toutesPortees!: boolean;
	toutesOperations!: boolean;
	
	constructor() {
		super();
		this.parcId = -1;
	}
}

class Row extends UserPreferenceNotificationBase {
	parcName!: string;
	toutesPortees!: boolean;
	toutesOperations!: boolean;
	
	constructor() {
		super();
	}
}

@Component({
	selector: 'user-preferences-notifications',
	templateUrl: 'user-preferences-notifications.html',
	styleUrls: ['user-preferences-notifications.css'],
})
export class UserPreferencesNotifications {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	waitingForServer = false;
	
	// Donnees bindees sur la vue
	user!: UserTLR;
	header!: Header;
	rows!: Row[];

	constructor(
		private dialog: MatDialog,

		@Inject(EventBusService) eventBusService: EventBusService,
		
		@Inject(ParcService) parcService: ParcService,
		@Inject(UserService) private userService: UserService,
	) {
		/////////////////////////////////////////
		// Prechargement des donnees statiques //
		/////////////////////////////////////////
		
		let user$ = userService.getCurrentUserTLR();
		
		let antenneId$ = user$.pipe(
			map(
				(user) => user.antenneId
			)
		)
		
		let parcsOfUser$ = antenneId$.pipe(
			mergeMap(
				(antenneId) => (antenneId === null || antenneId === undefined) ? of([]) : parcService.getParcTLRByAntenne(antenneId)
			)
		);

		///////////////////////////////////////////////////////////////////////////////////////////////////////
		// Initialisation du composant lors de son affichage, a partir des informations de contexte fournies //
		///////////////////////////////////////////////////////////////////////////////////////////////////////
		let init$ = new Subject<void>();
		eventBusService.observe(Events.EditUserPreferencesNotificationsRequested, () => init$.next());
		
		let initWithPreferences$ = combineLatest([user$, parcsOfUser$, init$]).pipe(
			mergeMap(
				([user, parcsOfUser, _]) => {
					// Ne charger les preferences de l'utilisateur que quand il l'ecran s'affiche, pas avant
					let preferences$ = userService.getPreferencesNotifications(user.id);
					
					// Fabriquer les lignes a afficher dans le tableau
					let rows$ = preferences$.pipe(
						map(
							(preferences) => parcsOfUser.map(
								(parc) => {
									// Creer la base de la ligne a afficher pour ce parc
									let row = {
											parcId: parc.id,
											parcName: parc.code + ' (' + parc.description + ')',
											toutesPortees: false,
											local: false,
											distant: false,
											toutesOperations: false,
											validation: false,
											fermeture: false,
											refus: false,
										} as Row;
									
									// Rechercher si le parc est dans les preferences
									let index = preferences.findIndex($ => $.parcId === parc.id);
									
									// Si c'est le cas, mettre les valeurs de cette preference dans la ligne
									if (index > -1) {
										let preference = preferences[index];
										row.toutesPortees = preference.local && preference.distant;
										row.local = preference.local;
										row.distant = preference.distant;
										row.toutesOperations = preference.validation && preference.fermeture && preference.refus;
										row.validation = preference.validation;
										row.fermeture = preference.fermeture;
										row.refus = preference.refus;
									}
									
									return row;
								}
							)
						)
					);
					
					return forkJoin({
						user: of(user),
						rows: rows$,
					});
				}
			)
		);
		
		initWithPreferences$.subscribe(
			({user, rows}) => {
				this.user = user;
				this.rows = rows;
				
				this.header = {
					parcId: -1,
					parcName: '',
					toutesPortees: this.rows.every($ => $.toutesPortees),
					local: this.rows.every($ => $.local),
					distant: this.rows.every($ => $.distant),
					toutesOperations: this.rows.every($ => $.toutesOperations),
					validation: this.rows.every($ => $.validation),
					fermeture: this.rows.every($ => $.fermeture),
					refus: this.rows.every($ => $.refus),
				} as Row;
				
				this.displaySubject.next(true);
			}
		)
	}

	@Input() id = 'editUserPreferencesNotifications';
	
	onWindowClose() {
		this.rows = [];
	}
	
	private updateToutesPortees(row: Row) {
		row.toutesPortees = row.local && row.distant;
		this.header.toutesPortees = this.rows.every($ => $.toutesPortees);
	}
	
	private updateToutesOperations(row: Row) {
		row.toutesOperations = row.validation && row.fermeture && row.refus;
		this.header.toutesOperations = this.rows.every($ => $.toutesOperations);
	}

	onHeaderToutesPorteesChanged(value: boolean) {
		if (value !== this.header.toutesPortees) {
			this.header.toutesPortees = value;
			this.header.local = value;
			this.header.distant = value;
			this.rows.forEach(row => {
				row.toutesPortees = value;
				row.local = value;
				row.distant = value;
			});
		}
	}

	onHeaderLocalChanged(value: boolean) {
		if (value !== this.header.local) {
			this.header.local = value;
			this.rows.forEach(row => {
				row.local = value;
				this.updateToutesPortees(row);
			});
		}
	}

	onHeaderDistantChanged(value: boolean) {
		if (value !== this.header.distant) {
			this.header.distant = value;
			this.rows.forEach(row => {
				row.distant = value;
				this.updateToutesPortees(row);
			});
		}
	}

	onHeaderToutesOperationsChanged(value: boolean) {
		if (value !== this.header.toutesOperations) {
			this.header.toutesOperations = value;
			this.header.validation = value;
			this.header.fermeture = value;
			this.header.refus = value;
			this.rows.forEach(row => {
				row.toutesOperations = value;
				row.validation = value;
				row.fermeture = value;
				row.refus = value;
			});
		}
	}

	onHeaderValidationChanged(value: boolean) {
		if (value !== this.header.validation) {
			this.header.validation = value;
			this.rows.forEach(row => {
				row.validation = value;
				this.updateToutesOperations(row);
			});
		}
	}

	onHeaderFermetureChanged(value: boolean) {
		if (value !== this.header.fermeture) {
			this.header.fermeture = value;
			this.rows.forEach(row => {
				row.fermeture = value;
				this.updateToutesOperations(row);
			});
		}
	}

	onHeaderRefusChanged(value: boolean) {
		if (value !== this.header.refus) {
			this.header.refus = value;
			this.rows.forEach(row => {
				row.refus = value;
				this.updateToutesOperations(row);
			});
		}
	}

	onToutesPorteesChanged(row: Row, value: boolean) {
		if (value !== row.toutesPortees) {
			row.local = value;
			this.header.local = this.rows.every($ => $.local);
			row.distant = value;
			this.header.distant = this.rows.every($ => $.distant);
			this.updateToutesPortees(row);
		}
	}

	onLocalChanged(row: Row, value: boolean) {
		if (value !== row.local) {
			row.local = value;
			this.updateToutesPortees(row);
			this.header.local = this.rows.every($ => $.local);
		}
	}

	onDistantChanged(row: Row, value: boolean) {
		if (value !== row.distant) {
			row.distant = value;
			this.updateToutesPortees(row);
			this.header.distant = this.rows.every($ => $.distant);
		}
	}

	onToutesOperationsChanged(row: Row, value: boolean) {
		if (value !== row.toutesOperations) {
			row.validation = value;
			this.header.validation = this.rows.every($ => $.validation);
			row.fermeture = value;
			this.header.fermeture = this.rows.every($ => $.fermeture);
			row.refus = value;
			this.header.refus = this.rows.every($ => $.refus);
			this.updateToutesOperations(row);
		}
	}

	onValidationChanged(row: Row, value: boolean) {
		if (value !== row.validation) {
			row.validation = value;
			this.updateToutesOperations(row);
			this.header.validation = this.rows.every($ => $.validation);
		}
	}

	onFermetureChanged(row: Row, value: boolean) {
		if (value !== row.fermeture) {
			row.fermeture = value;
			this.updateToutesOperations(row);
			this.header.fermeture = this.rows.every($ => $.fermeture);
		}
	}

	onRefusChanged(row: Row, value: boolean) {
		if (value !== row.refus) {
			row.refus = value;
			this.updateToutesOperations(row);
			this.header.refus = this.rows.every($ => $.refus);
		}
	}
	
	close() {
		this.displaySubject.next(false);
	}
	
	save() {
		let updateBases = this.rows //
			.filter(row => row.local || row.distant || row.validation || row.fermeture || row.refus) //
			.map(row => ({
					parcId: row.parcId,
					local: row.local,
					distant: row.distant,
					validation: row.validation,
					fermeture: row.fermeture,
					refus: row.refus,
				} as UserPreferenceNotificationBase)
			);
		
		this.waitingForServer = true;
		this.userService.updatePreferencesNotifications(this.user.id, updateBases).subscribe(
			(_) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de l'enregistrement des preferences",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors de l'enregistrement des preferences", error);
			}
		);
	}
}